import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setErrorMessage } from "../slices/messageSlice";
import { GetRankingByDate } from "../services/RankService";
import RankingFilter from "../components/RankingFilter";
import RankingTabs from "../components/RankingTabs";
import DailyRankingTable from "../components/DailyRankingTable";
import DaysRankingTable from "../components/DaysRankingTable";
import dayjs from "dayjs";
import MonthylRankingTable from "../components/MonthylRankingTable";

const Ranking = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [topUsers, setTopUsers] = useState(10);
  const [activeTab, setActiveTab] = useState("daily");
  const [dailyData, setDailyData] = useState([]);

  // Load filters from localStorage on component mount
  useEffect(() => {
    const savedFilters = localStorage.getItem("rankingFilters");
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setStartDate(filters.startDate || "");
        setEndDate(filters.endDate || "");
        setTopUsers(filters.topUsers || 10);
        setActiveTab(filters.activeTab || "daily");
      } catch (error) {
        console.error("Error parsing saved filters:", error);
        setDefaultDates();
      }
    } else {
      setDefaultDates();
    }
  }, []);

  useEffect(() => {
    const filters = {
      startDate,
      endDate,
      topUsers,
      activeTab,
    };
    localStorage.setItem("rankingFilters", JSON.stringify(filters));
  }, [startDate, endDate, topUsers, activeTab]);

  const setDefaultDates = () => {
    const today = dayjs();
    const startOfWeek = today.startOf("week").add(1, "day");
    const endOfWeek = startOfWeek.add(6, "day");

    setStartDate(startOfWeek.format("YYYY-MM-DD"));
    setEndDate(endOfWeek.format("YYYY-MM-DD"));
  };

  // Calculate date range for N days tab
  const getDateRangeForDays = (days) => {
    const endDate = dayjs();
    const startDate = endDate.subtract(days - 1, "day");
    return {
      start: startDate.format("YYYY-MM-DD"),
      end: endDate.format("YYYY-MM-DD"),
    };
  };

  // Auto-load data when component mounts with saved filters
  useEffect(() => {
    if (activeTab === "daily" && startDate && endDate) {
      fetchDailyRankings();
    }
    // Note: 10days, 20days, 30days tabs use DaysRankingTable which fetches its own data
  }, [startDate, endDate, topUsers, activeTab]);

  const fetchDailyRankings = async () => {
    try {
      setLoading(true);
      const response = await GetRankingByDate(
        startDate,
        endDate,
        topUsers,
        "",
        "",
        ""
      );
      if (response.status_code === 200) {
        console.log(response.data.listDayDetail);
        setDailyData(response.data.listDayDetail);
      } else {
        dispatch(
          setErrorMessage(
            response.message || "Có lỗi xảy ra khi lấy bảng xếp hạng"
          )
        );
      }
    } catch (error) {
      dispatch(setErrorMessage("Có lỗi xảy ra khi lấy bảng xếp hạng"));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "daily") {
      fetchDailyRankings();
    }
    // Note: 10days, 20days, 30days tabs use DaysRankingTable which fetches its own data
  };

  const handleFilter = () => {
    if (activeTab === "daily") {
      fetchDailyRankings();
    }
    // Note: 10days, 20days, 30days tabs use DaysRankingTable which fetches its own data
  };

  return (
    <div className="pt-10 h-full min-h-screen flex justify-center overflow-y-auto p-16 bg-white text-black dark:bg-gray-900 dark:text-white">
      <div className="w-[1400px] bg-white rounded dark:bg-gray-800 shadow-lg p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
            Bảng Xếp Hạng
          </h1>

          <RankingFilter
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            topUsers={topUsers}
            setTopUsers={setTopUsers}
            loading={loading}
            onFilter={handleFilter}
            activeTab={activeTab}
          />

          <div className="mt-8">
            <RankingTabs activeTab={activeTab} setActiveTab={handleTabChange} />

            <div className="mt-6">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <>
                  {activeTab === "daily" && (
                    <DailyRankingTable weekData={dailyData} />
                  )}
                  {activeTab === "10days" && (
                    <DaysRankingTable days={10} startDate={startDate} endDate={endDate} />
                  )}
                  {activeTab === "20days" && (
                    <DaysRankingTable days={20} startDate={startDate} endDate={endDate} />
                  )}
                  {activeTab === "30days" && (
                    <DaysRankingTable days={30} startDate={startDate} endDate={endDate} />
                  )}
                  {activeTab === "monthly" && (
                    <MonthylRankingTable
                      datePostedAtFrom={startDate}
                      datePostedAtTo={endDate}
                      topUsers={topUsers}
                    />
                  )}
                  {/* {activeTab === "efficiency" && (
                    <EfficiencyTable
                      datePostedAtFrom={startDate}
                      datePostedAtTo={endDate}
                      topUsers={topUsers}
                    />
                  )} */}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ranking;
